import { useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import {
  Alert,
  Badge,
  Button,
  Card,
  CheckCircleIcon,
  Cluster,
  DocumentIcon,
  Page,
  Stack,
  StatStrip,
  TextArea,
  cx,
} from '@integra/ui';
import { useIngestManifest } from '../api/hooks.js';
import type { ContentManifest, IngestBatchResult } from '../api/types.js';

/**
 * AI Ingress — Import (docs/specs/ai-ingress.md §4A). Paste a Content Manifest
 * (JSON) or upload a `.json`/`.csv` and INTEGRA creates one DRAFT asset per post.
 *
 * Honest by design (§2.7): imported content lands as drafts and is NEVER
 * published automatically — a human reviews and publishes from Assets. Available
 * on every plan; the only limit is the workspace's asset quota.
 *
 * The CSV parser and post-normalisation are ported from `scripts/import-manifest.ts`
 * so the paste box, an upload, and the script all read a manifest the same way.
 * All data access is through the hooks layer (§21.4) — no fetch here.
 */

/** CSV cells for these keys are split into arrays on the pipe character. */
const LIST_KEYS = new Set(['hashtags', 'channels', 'images']);
/** CSV cells for these keys are coerced to numbers. */
const NUMBER_KEYS = new Set(['width', 'height', 'byteSize', 'durationSeconds']);

/** A minimal RFC-4180-ish CSV parser: quoted fields, escaped quotes, newlines. */
function parseCsv(text: string): Record<string, string>[] {
  const rows: string[][] = [];
  let field = '';
  let row: string[] = [];
  let inQuotes = false;
  for (let i = 0; i < text.length; i++) {
    const c = text[i];
    if (inQuotes) {
      if (c === '"') {
        if (text[i + 1] === '"') {
          field += '"';
          i++;
        } else {
          inQuotes = false;
        }
      } else {
        field += c;
      }
    } else if (c === '"') {
      inQuotes = true;
    } else if (c === ',') {
      row.push(field);
      field = '';
    } else if (c === '\n' || c === '\r') {
      if (c === '\r' && text[i + 1] === '\n') i++;
      row.push(field);
      field = '';
      if (row.some((v) => v !== '')) rows.push(row);
      row = [];
    } else {
      field += c;
    }
  }
  if (field !== '' || row.length > 0) {
    row.push(field);
    if (row.some((v) => v !== '')) rows.push(row);
  }
  const header = rows.shift();
  if (header === undefined) return [];
  return rows.map((cells) =>
    Object.fromEntries(header.map((h, idx) => [h.trim(), (cells[idx] ?? '').trim()])),
  );
}

/** Turn a raw CSV row into a post object (numbers, lists, drop blanks). */
function rowToPost(rawRow: Record<string, string>): Record<string, unknown> {
  const post: Record<string, unknown> = {};
  for (const [key, value] of Object.entries(rawRow)) {
    if (value === '') continue;
    if (LIST_KEYS.has(key)) {
      post[key] = value.split('|').map((v) => v.trim());
    } else if (NUMBER_KEYS.has(key)) {
      const n = Number(value);
      post[key] = Number.isFinite(n) ? n : value;
    } else {
      post[key] = value;
    }
  }
  return post;
}

/** Read posts from pasted/loaded JSON: an array, or `{ posts: [ … ] }`, or a
 * full manifest object. Throws a friendly message the screen surfaces. */
function parsePostsFromJson(text: string): Record<string, unknown>[] {
  let parsed: unknown;
  try {
    parsed = JSON.parse(text);
  } catch {
    throw new Error('That is not valid JSON. Check for a stray comma or missing quote.');
  }
  const list = Array.isArray(parsed)
    ? parsed
    : typeof parsed === 'object' &&
        parsed !== null &&
        Array.isArray((parsed as { posts?: unknown }).posts)
      ? (parsed as { posts: unknown[] }).posts
      : null;
  if (list === null) {
    throw new Error('JSON must be an array of posts, or an object with a "posts" array.');
  }
  return list.map((p, i) => {
    if (typeof p !== 'object' || p === null || Array.isArray(p)) {
      throw new Error(`Post #${String(i + 1)} is not an object.`);
    }
    return p as Record<string, unknown>;
  });
}

const CSV_TEMPLATE =
  'type,title,body,imageUrl,visibility\n' +
  'article,"Why we quote 20 hours","Every power bank lists the lab number. We list the one you get.",,private\n' +
  'image,"Autumn hamper","",https://example.com/hero.jpg,private\n';

const JSON_EXAMPLE =
  '{\n  "manifestVersion": "1",\n  "posts": [\n' +
  '    { "type": "article", "title": "Why we quote 20 hours", "body": "Your post text here." },\n' +
  '    { "type": "image", "title": "Autumn hamper", "imageUrl": "https://example.com/hero.jpg" }\n' +
  '  ]\n}';

/** Best-effort count of how many posts a manifest holds, for pre-import feedback. */
function countPosts(raw: string, isCsv: boolean): number | null {
  if (raw.trim() === '') return null;
  try {
    return (isCsv ? parseCsv(raw).map(rowToPost) : parsePostsFromJson(raw)).length;
  } catch {
    return null;
  }
}

function UploadIcon() {
  return (
    <svg
      viewBox="0 0 24 24"
      width="28"
      height="28"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.7"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4M17 8l-5-5-5 5M12 3v13" />
    </svg>
  );
}

export function ImportPage() {
  const { workspaceId = '' } = useParams<{ workspaceId: string }>();
  const ingest = useIngestManifest(workspaceId);

  const [raw, setRaw] = useState('');
  const [isCsv, setIsCsv] = useState(false);
  const [fileName, setFileName] = useState<string | null>(null);
  const [parseError, setParseError] = useState<string | null>(null);
  const [batch, setBatch] = useState<IngestBatchResult | null>(null);
  const [dragOver, setDragOver] = useState(false);
  const [showAdvanced, setShowAdvanced] = useState(false);

  const loadFile = (file: File): void => {
    setFileName(file.name);
    setIsCsv(file.name.toLowerCase().endsWith('.csv'));
    setParseError(null);
    setBatch(null);
    void file.text().then(
      (text) => setRaw(text),
      () => setParseError('Could not read that file.'),
    );
  };

  const onFile = (event: React.ChangeEvent<HTMLInputElement>): void => {
    const file = event.target.files?.[0];
    if (file !== undefined) loadFile(file);
  };

  const downloadTemplate = (): void => {
    const blob = new Blob([CSV_TEMPLATE], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'integra-import-template.csv';
    a.click();
    URL.revokeObjectURL(url);
  };

  const fillExample = (): void => {
    setRaw(JSON_EXAMPLE);
    setIsCsv(false);
    setFileName(null);
    setParseError(null);
    setShowAdvanced(true);
  };

  const submit = (): void => {
    setParseError(null);
    setBatch(null);
    if (raw.trim() === '') {
      setParseError('Paste a manifest or choose a file first.');
      return;
    }
    let posts: Record<string, unknown>[];
    try {
      posts = isCsv ? parseCsv(raw).map(rowToPost) : parsePostsFromJson(raw);
    } catch (error) {
      setParseError(error instanceof Error ? error.message : 'Could not read the manifest.');
      return;
    }
    if (posts.length === 0) {
      setParseError('No posts found in that manifest.');
      return;
    }
    const manifest: ContentManifest = { manifestVersion: '1', posts };
    ingest.mutate(manifest, { onSuccess: (result) => setBatch(result) });
  };

  const reset = (): void => {
    setRaw('');
    setIsCsv(false);
    setFileName(null);
    setParseError(null);
    setBatch(null);
  };

  const created = batch?.results.filter((r) => r.assetId !== undefined) ?? [];
  const failed = batch?.results.filter((r) => r.assetId === undefined) ?? [];

  const detected = countPosts(raw, isCsv);

  return (
    <Page
      title="Import"
      icon={DocumentIcon}
      description="Bring content you already have — from an AI session, a spreadsheet, or another tool — in as drafts to review before publishing."
      stats={
        batch !== null ? (
          <StatStrip
            stats={[
              {
                icon: CheckCircleIcon,
                label: 'Drafts created',
                value: created.length,
                tone: 'accent',
              },
              { icon: DocumentIcon, label: 'Skipped', value: failed.length },
            ]}
          />
        ) : undefined
      }
    >
      <Stack gap="lg">
        {batch === null ? (
          <Card>
            <Stack gap="lg">
              {/* Primary path: drop or browse a file. */}
              <label
                htmlFor="import-file"
                onDragOver={(e) => {
                  e.preventDefault();
                  setDragOver(true);
                }}
                onDragLeave={() => setDragOver(false)}
                onDrop={(e) => {
                  e.preventDefault();
                  setDragOver(false);
                  const f = e.dataTransfer.files?.[0];
                  if (f !== undefined) loadFile(f);
                }}
                className={cx(
                  'flex cursor-pointer flex-col items-center justify-center gap-2 rounded-xl border-2 border-dashed px-6 py-10 text-center transition-colors duration-fast',
                  dragOver
                    ? 'border-gold-400 bg-gold-200/40'
                    : 'border-edge-strong bg-surface-sunken hover:border-navy-400',
                )}
              >
                <span className="text-navy-500">
                  <UploadIcon />
                </span>
                <p className="text-sm font-semibold text-navy-700">Drag a CSV or JSON file here</p>
                <p className="text-xs text-ink-muted">
                  or click to browse — products, articles, or images
                </p>
                <input
                  id="import-file"
                  type="file"
                  accept=".json,.csv"
                  onChange={onFile}
                  className="sr-only"
                />
              </label>

              {/* Get-started helpers + the honest one-liner. */}
              <Cluster gap="sm" align="center">
                <button
                  type="button"
                  onClick={downloadTemplate}
                  className="text-sm font-medium text-navy-700 hover:underline"
                >
                  Download CSV template
                </button>
                <span className="text-edge-strong">·</span>
                <button
                  type="button"
                  onClick={fillExample}
                  className="text-sm font-medium text-navy-700 hover:underline"
                >
                  See a JSON example
                </button>
                <span className="ml-auto text-xs text-ink-muted">
                  Imports land as drafts — nothing publishes automatically.
                </span>
              </Cluster>

              {/* Live feedback once something is loaded. */}
              {(fileName !== null || raw.trim() !== '') && (
                <div className="flex items-center justify-between gap-3 rounded-md border border-edge bg-surface px-3 py-2">
                  <span className="min-w-0 truncate text-sm text-ink">
                    {fileName !== null ? (
                      <>
                        Loaded <span className="font-mono text-xs">{fileName}</span>
                      </>
                    ) : (
                      'Manifest ready'
                    )}
                    {detected !== null ? (
                      <span className="text-ink-muted">
                        {' '}
                        · {detected} post{detected === 1 ? '' : 's'} detected
                      </span>
                    ) : (
                      <span className="text-state-warning"> · not readable yet</span>
                    )}
                  </span>
                  <Button variant="ghost" size="sm" onClick={reset}>
                    Clear
                  </Button>
                </div>
              )}

              {/* The raw manifest, for those who want it — hidden by default. */}
              <div>
                <button
                  type="button"
                  onClick={() => setShowAdvanced((v) => !v)}
                  className="text-xs font-semibold uppercase tracking-wide text-ink-muted transition-colors hover:text-navy-700"
                >
                  {showAdvanced ? '▾' : '▸'} Advanced — paste a manifest
                </button>
                {showAdvanced && (
                  <div className="mt-2">
                    <TextArea
                      id="import-manifest"
                      rows={10}
                      value={raw}
                      onChange={(e) => {
                        setRaw(e.target.value);
                        setIsCsv(false);
                        setFileName(null);
                        setParseError(null);
                      }}
                      placeholder={JSON_EXAMPLE}
                    />
                    <p className="mt-1 text-xs text-ink-muted">
                      A JSON array of posts, an object with a “posts” array, or CSV with a header
                      row. type and visibility become the asset; every other column becomes content.
                    </p>
                  </div>
                )}
              </div>

              {parseError !== null && (
                <Alert tone="error" title="That file could not be read">
                  {parseError}
                </Alert>
              )}
              {ingest.error instanceof Error && (
                <Alert tone="error" title="Import failed">
                  {ingest.error.message}
                </Alert>
              )}

              <Cluster gap="md">
                <Button onClick={submit} loading={ingest.isPending} disabled={raw.trim() === ''}>
                  Import as drafts
                </Button>
              </Cluster>
            </Stack>
          </Card>
        ) : (
          <Card title="Import complete">
            <Stack gap="md">
              <Cluster gap="md" align="center">
                <Badge tone="success">{created.length} draft(s) created</Badge>
                {failed.length > 0 && <Badge tone="warning">{failed.length} skipped</Badge>}
                <span className="font-mono text-xs text-ink-muted">batch {batch.batchId}</span>
              </Cluster>

              <p className="text-sm text-ink-muted">
                Nothing has been published. Review the new drafts in Assets, then publish or
                schedule them when you are ready.
              </p>

              <div className="overflow-hidden rounded-md border border-edge">
                <ul className="divide-y divide-edge">
                  {batch.results.map((result, index) => (
                    <li
                      key={result.assetId ?? result.ref ?? String(index)}
                      className="flex items-center justify-between gap-4 px-3 py-2 text-sm"
                    >
                      <span className="min-w-0 truncate font-mono text-xs text-ink-muted">
                        {result.ref ?? `post #${String(index + 1)}`}
                      </span>
                      {result.assetId !== undefined ? (
                        <Link
                          to={`/w/${workspaceId}/assets/${result.assetId}`}
                          className="min-w-0 truncate font-mono text-xs font-semibold text-navy-700 hover:underline"
                        >
                          {result.assetId}
                        </Link>
                      ) : (
                        <span className="min-w-0 truncate text-xs text-state-error">
                          {result.error ?? 'Not created'}
                        </span>
                      )}
                    </li>
                  ))}
                </ul>
              </div>

              <Cluster gap="md">
                <Link
                  to={`/w/${workspaceId}/assets`}
                  className="inline-flex items-center gap-1.5 rounded-md bg-gold-400 px-3.5 py-1.5 text-sm font-semibold text-navy-900 transition-colors duration-fast hover:bg-gold-500"
                >
                  Review drafts in Assets →
                </Link>
                <Button variant="ghost" onClick={reset}>
                  Import another
                </Button>
              </Cluster>
            </Stack>
          </Card>
        )}
      </Stack>
    </Page>
  );
}
